import { SHOW_SNACK } from "@/store/snack.module";

function roundMoney(value, decimals = 4) {
  const amount = Number(value || 0);
  const factor = 10 ** decimals;
  return Math.round(amount * factor) / factor;
}

/**
 * @param {object} extra
 * @param {Array<{id: number, slug: string}>} [allocationMethodOptions]
 */
export function resolveExtraCostMethodSlug(extra, allocationMethodOptions = []) {
  if (extra.allocation_method_slug) {
    return extra.allocation_method_slug;
  }
  if (extra.allocation_method?.slug) {
    return extra.allocation_method.slug;
  }
  const match = allocationMethodOptions.find(
    (m) => Number(m.id) === Number(extra.allocation_method_id)
  );
  return match?.slug || "by_value";
}

export function computeExtraCostLineAllocations(
  lines,
  extra,
  quantityKey = "quantity",
  allocationMethodOptions = []
) {
  const methodSlug = resolveExtraCostMethodSlug(extra, allocationMethodOptions);
  const amountNet = Number(extra.amount_net || 0);
  const activeLines = (lines || []).filter((l) => l.stock_product_id);

  if (amountNet <= 0 || !activeLines.length) {
    return activeLines.map((_, index) => ({ line_index: index, amount_net: 0 }));
  }

  if (methodSlug === "manual") {
    return activeLines.map((line, index) => ({
      line_index: index,
      amount_net: roundMoney(line.extra_allocations?.[extra._key] ?? 0),
    }));
  }

  const weights = activeLines.map((line) => {
    const qty = Math.abs(Number(line[quantityKey] ?? line.quantity ?? 0));
    const lineNet =
      line.line_total_net != null && line.line_total_net !== ""
        ? Number(line.line_total_net)
        : qty * Number(line.unit_cost_net || 0);

    if (methodSlug === "by_weight") {
      return qty;
    }
    if (methodSlug === "equal") {
      return 1;
    }
    return Math.abs(lineNet);
  });

  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight <= 0) {
    return activeLines.map((_, index) => ({ line_index: index, amount_net: 0 }));
  }

  let allocatedSum = 0;
  const lastIndex = activeLines.length - 1;

  return activeLines.map((_, index) => {
    let share;
    if (index === lastIndex) {
      share = roundMoney(amountNet - allocatedSum);
    } else {
      share = roundMoney(amountNet * (weights[index] / totalWeight));
      allocatedSum += share;
    }
    return { line_index: index, amount_net: share };
  });
}

export function sumExtraAllocations(allocations) {
  return roundMoney(
    (allocations || []).reduce((sum, row) => sum + Number(row.amount_net || 0), 0)
  );
}

export function parseManualAmountInput(raw) {
  if (raw == null || raw === "") {
    return 0;
  }
  if (typeof raw === "object" && raw.target != null) {
    return roundMoney(Number(String(raw.target.value).replace(",", ".")) || 0);
  }
  return roundMoney(Number(String(raw).replace(",", ".")) || 0);
}

export default {
  methods: {
    roundExtraMoney(value) {
      return roundMoney(value);
    },
    computeExtraCostLineAllocations(lines, extra, quantityKey) {
      return computeExtraCostLineAllocations(
        lines,
        extra,
        quantityKey,
        this.allocationMethodOptions || []
      );
    },
    sumExtraAllocations(allocations) {
      return sumExtraAllocations(allocations);
    },
    extraCostMethodSlug(extra) {
      return resolveExtraCostMethodSlug(extra, this.allocationMethodOptions || []);
    },
    isManualExtraCost(extra) {
      return this.extraCostMethodSlug(extra) === "manual";
    },
    canEditExtraLineAllocation(extra) {
      return (
        this.canSave &&
        this.isManualExtraCost(extra) &&
        Number(extra.amount_net || 0) > 0
      );
    },
    previewExtraLineAmount(lines, extra, lineIndex, quantityKey) {
      const rows = this.computeExtraCostLineAllocations(
        lines,
        extra,
        quantityKey || this.lineQuantityKey
      );
      const row = rows.find((r) => r.line_index === lineIndex);
      return row ? row.amount_net : 0;
    },
    extraAllocatedTotal(extra, lines, quantityKey) {
      if (this.isManualExtraCost(extra)) {
        return this.sumManualAllocations(extra);
      }
      const sourceLines = lines || this.productLines || this.lines;
      const rows = this.computeExtraCostLineAllocations(
        sourceLines,
        extra,
        quantityKey || this.lineQuantityKey
      );
      return sumExtraAllocations(rows);
    },
    sumManualAllocations(extra, excludeProductIndex = -1) {
      let sum = 0;
      (this.productLines || []).forEach((line, idx) => {
        if (idx === excludeProductIndex) {
          return;
        }
        sum += Number(line.extra_allocations?.[extra._key] || 0);
      });
      return roundMoney(sum);
    },
    maxManualAllocationForLine(extra, line) {
      const productIndex = this.productLineIndex(line);
      if (productIndex < 0) {
        return 0;
      }
      const total = Number(extra.amount_net || 0);
      if (total <= 0) {
        return 0;
      }
      const others = this.sumManualAllocations(extra, productIndex);
      return roundMoney(Math.max(0, total - others));
    },
    getManualAllocationAmount(line, extra) {
      const amount = line.extra_allocations?.[extra._key];
      if (amount == null || amount === "") {
        return "";
      }
      return amount;
    },
    /** While typing: store value as entered (no clamp). */
    setManualAllocationAmount(line, extra, rawValue) {
      if (!this.isManualExtraCost(extra)) {
        return;
      }
      if (this.productLineIndex(line) < 0) {
        return;
      }

      if (!line.extra_allocations) {
        this.$set(line, "extra_allocations", {});
      }

      const total = Number(extra.amount_net || 0);
      if (total <= 0) {
        this.$set(line.extra_allocations, extra._key, 0);
        return;
      }

      if (rawValue === "" || rawValue == null) {
        this.$delete(line.extra_allocations, extra._key);
        return;
      }

      const parsed = parseManualAmountInput(rawValue);
      this.$set(line.extra_allocations, extra._key, parsed);
    },
    /**
     * On blur: clamp to max = service total − sum(other lines).
     */
    finalizeManualAllocationAmount(line, extra) {
      if (!this.isManualExtraCost(extra)) {
        return;
      }
      if (this.productLineIndex(line) < 0) {
        return;
      }

      if (!line.extra_allocations) {
        this.$set(line, "extra_allocations", {});
      }

      const total = Number(extra.amount_net || 0);
      if (total <= 0) {
        this.$set(line.extra_allocations, extra._key, 0);
        return;
      }

      const raw = line.extra_allocations[extra._key];
      const current =
        raw == null || raw === "" ? 0 : roundMoney(Number(raw) || 0);
      const maxAllowed = this.maxManualAllocationForLine(extra, line);
      const clamped = roundMoney(Math.min(Math.max(0, current), maxAllowed));

      this.$set(line.extra_allocations, extra._key, clamped);
    },
    finalizeAllManualAllocations() {
      (this.extraCosts || []).forEach((extra) => {
        if (!this.isManualExtraCost(extra)) {
          return;
        }
        (this.productLines || []).forEach((line) => {
          if (line.stock_product_id) {
            this.finalizeManualAllocationAmount(line, extra);
          }
        });
      });
    },
    rememberExtraAmountNet(extra) {
      extra._amountNetBeforeEdit = Number(extra.amount_net || 0);
    },
    onExtraAmountNetChange(extra) {
      if (!this.isManualExtraCost(extra)) {
        return;
      }

      const total = Number(extra.amount_net || 0);
      const allocated = this.sumManualAllocations(extra);

      if (allocated <= 0.0001) {
        return;
      }

      if (total < allocated - 0.0001) {
        this.clearExtraAllocationsOnLines(extra._key);
        if (this.$store) {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("STOCK.RECEIPTS.EXTRA_ALLOCATIONS_CLEARED"),
            color: "info",
          });
        }
      }
    },
    clearExtraAllocationsOnLines(extraKey) {
      (this.productLines || []).forEach((line) => {
        if (!line.extra_allocations) {
          return;
        }
        if (line.extra_allocations[extraKey] != null) {
          this.$delete(line.extra_allocations, extraKey);
        }
      });
    },
    manualAllocationIsOverTotal(extra) {
      const total = Number(extra.amount_net || 0);
      if (total <= 0) {
        return false;
      }
      return this.sumManualAllocations(extra) > total + 0.0001;
    },
    manualAllocationIsUnderTotal(extra) {
      const total = Number(extra.amount_net || 0);
      if (total <= 0) {
        return false;
      }
      return this.sumManualAllocations(extra) < total - 0.0001;
    },
    manualAllocPlaceholder(line, extra) {
      if (!this.isManualExtraCost(extra) || Number(extra.amount_net || 0) <= 0) {
        return "";
      }
      const max = this.maxManualAllocationForLine(extra, line);
      if (max <= 0) {
        return this.$t("STOCK.RECEIPTS.EXTRA_ALLOC_FULL");
      }
      return this.$t("STOCK.RECEIPTS.EXTRA_ALLOC_MAX", { max: this.formatNum(max) });
    },
    manualAllocRemaining(extra) {
      const total = Number(extra.amount_net || 0);
      return roundMoney(Math.max(0, total - this.sumManualAllocations(extra)));
    },
    buildExtraCostsPayload(extraCosts, lines, quantityKey) {
      const qKey = quantityKey || this.lineQuantityKey;
      const activeLines = (lines || []).filter((l) => l.stock_product_id);

      return (extraCosts || []).map((extra) => {
        const allocationRows = this.computeExtraCostLineAllocations(
          activeLines,
          extra,
          qKey
        );

        return {
          extra_cost_type_id: extra.extra_cost_type_id,
          allocation_method_id: extra.allocation_method_id,
          description: extra.description,
          amount_net: Number(extra.amount_net || 0),
          vat_rate: extra.vat_rate,
          supplier_company_id: extra.supplier_company_id,
          line_allocations: allocationRows,
        };
      });
    },
    nextExtraCostKey() {
      this._extraCostKeySeq = (this._extraCostKeySeq || 0) + 1;
      return `ec-${this._extraCostKeySeq}`;
    },
  },
};
